import React, { useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AiPlanGenerator, AiPlanGeneratorContext } from '@components/organisms/AiPlanGenerator';
import { useProject } from '@hooks/api/projects/useProject';
import { useImportProjectPlanDraft } from '@hooks/api/projectPlanDrafts';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { roleRank, TEMPLATE_ACTIONS_MIN_RANK } from '@constants/roleHierarchy';
import { useAiPlanningAvailable } from '@hooks/api/projectPlanDrafts';
import { useCanWrite } from '@hooks/useCanWrite';
import { useTranslation } from '@hooks/useTranslation';
import { Redirect } from 'react-router-dom';

/**
 * Sprint 31, story-161. Serves both `/projects/:id/plan/ai` (existing-project context, commit =
 * import) and `/templates/generate` (template context, commit = save-as-template only) — same
 * organism, different `context`. Standalone entry points (schedule accordion, Timeline card,
 * Templates page) all land here.
 */
export const AiPlanPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const location = useLocation();
  const { role } = useAppSelector((state: RootState) => state.user);
  const t = useTranslation('aiPlan');
  const { data: project } = useProject(id);
  const { data: aiPlanningAvailable, isLoading: isLoadingAvailability } = useAiPlanningAvailable();
  const importPlanDraftMutation = useImportProjectPlanDraft();
  const { canWrite } = useCanWrite();
  const [isCommitting, setIsCommitting] = useState(false);

  const draftId = useMemo(() => new URLSearchParams(location.search).get('draft') || undefined, [location.search]);

  const context: AiPlanGeneratorContext = id ? { kind: 'project', projectId: id } : { kind: 'template' };

  const canAccessPage = roleRank(role) >= TEMPLATE_ACTIONS_MIN_RANK && canWrite;
  if (!canAccessPage || (!isLoadingAvailability && aiPlanningAvailable === false)) {
    return <Redirect to={id ? `/projects/${id}` : '/templates'} />;
  }

  const handleCommit = async (plan: any, adjustEndDates: boolean) => {
    if (!id || !draftId) return;
    setIsCommitting(true);
    try {
      await importPlanDraftMutation.mutateAsync({
        draftId,
        projectId: id,
        startDate: (project?.startDate as unknown as string) || new Date().toISOString(),
        projectEndDate: (project?.endDate as unknown as string) || undefined,
        adjustEndDates,
      });
      history.push(`/projects/${id}`);
    } catch {
      // Import-mutation hook already surfaces its own toast; stay on this page so a retry
      // re-issues the same import against the same draftId/projectId, instead of letting the
      // rejection propagate uncaught through CommitBar/AiPlanGenerator (sprint 31 QA fix,
      // story-161 bug #1).
    } finally {
      setIsCommitting(false);
    }
  };

  const backTarget = id ? `/projects/${id}` : '/templates';

  return (
    <div className="max-w-6xl mx-auto py-6 px-4 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={() => history.push(backTarget)} className="text-gray-500 hover:text-gray-800 dark:hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{t.briefTitle}</h1>
      </div>

      <div className="flex-1 min-h-0">
        <AiPlanGenerator
          context={context}
          draftId={draftId}
          onDraftIdChange={(nextDraftId) => {
            const params = new URLSearchParams(location.search);
            if (nextDraftId) params.set('draft', nextDraftId);
            else params.delete('draft');
            history.replace({ search: params.toString() });
          }}
          onCommitPlan={context.kind === 'project' ? handleCommit : undefined}
          onCancel={() => history.push(backTarget)}
          isCommitting={isCommitting}
        />
      </div>
    </div>
  );
};

export default AiPlanPage;
